import React, { useMemo } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, GlobalStyles } from '@mui/material';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { getRoutes, isLoggingOut } from 'utils/auth';
import {
  IMPACT_HUB_BASE_PATH,
  LEGACY_AI_AVATAR_IMPACT_HUB_PATH,
} from 'app/pages/ImpactHubPage/utils/viewConfig';
import { SERVER_PROFILE_UPDATED_EVENT } from 'utils/serverProfileCache';

import { sliceKey, reducer } from './redux/headerSlice/slice';
import { headerSaga } from './redux/headerSlice/saga';
import { selectDealerSelected } from './redux/headerSlice/selectors';
import AppProvider from './globalContexts';
import { Layout } from './features/layout/Layout';
import { HomePage } from './features/home/HomePage';
import { TargetPage } from './features/TargetPage/TargetPage';
import {
  AUTOPILOT_BASE_PATH,
  LEGACY_AI_AVATAR_AUTOPILOT_PATH,
  LEGACY_AI_AVATAR_MEASURE_PATH,
} from 'app/pages/AutopilotPage/utils/autopilotPaths';
import { GeneratePage } from './features/GeneratePage/GeneratePage';
import { AnalyzePage } from './features/AnalyzePage/AnalyzePage';
import ConfigurePage from './features/ConfigurePage/ConfigurePage';
import { SettingsPage } from './features/SettingsPage/SettingsPage';
import { AdminPage } from './features/AdminPage/AdminPage';
import ContentPage from './features/ContentPage/ContentPage';
import { FeedbackPage } from './features/FeedbackPage/FeedbackPage';
import { NotFoundPage } from './features/NotFoundPage/NotFoundPage';
import DealerSelectionFallback from './sharedComponents/DealerSelectionFallback';
import { routesMap } from 'app/components/AuthRouter/authRoutes';
import ScorecardPage from 'app/pages/ScorecardPage';
import UnsoldPage from 'app/pages/UnsoldPage';
import UnsoldLeadDetailsPage from 'app/pages/UnsoldLeadDetailsPage';
import UserManagementPage from 'app/pages/UserManagementPage';

// Route to component mapping for NewAIAvatarLayout
const aiAvatarRouteMap: Record<string, React.ComponentType<any>> = {
  '/ai-avatar-layout': HomePage,
  '/ai-avatar-layout/target': TargetPage,
  '/ai-avatar-layout/generate': GeneratePage,
  '/ai-avatar-layout/analyze': AnalyzePage,
  '/ai-avatar-layout/configure': ConfigurePage,
  '/ai-avatar-layout/content': ContentPage,
  '/ai-avatar-layout/settings': SettingsPage,
  '/ai-avatar-layout/admin': AdminPage,
  '/ai-avatar-layout/feedback': FeedbackPage,
  '/ai-avatar-layout/scorecard': ScorecardPage,
  '/ai-avatar-layout/unsold': UnsoldPage,
  '/ai-avatar-layout/unsold/lead/:leadId': UnsoldLeadDetailsPage,
  '/ai-avatar-layout/user-management': UserManagementPage,
};

// Component name to route mapping (for resource_mappings)
const componentToRouteMap: Record<string, string> = {
  'AIAvatarHomePage': '/ai-avatar-layout',
  // Autopilot / Impact Hub are shared URLs served at app root — not under this Switch
  'AIAvatarAutopilotPage': AUTOPILOT_BASE_PATH,
  'AutopilotPage': AUTOPILOT_BASE_PATH,
  'AIAvatarMeasurePage': AUTOPILOT_BASE_PATH,
  'AskMiaAutopilotPage': `${AUTOPILOT_BASE_PATH}/ask-mia`,
  'AIAvatarTargetPage': '/ai-avatar-layout/target',
  'AIAvatarGeneratePage': '/ai-avatar-layout/generate',
  'AIAvatarAnalyzePage': '/ai-avatar-layout/analyze',
  'AIAvatarConfigurePage': '/ai-avatar-layout/configure',
  'AIAvatarContentPage': '/ai-avatar-layout/content',
  'AIAvatarSettingsPage': '/ai-avatar-layout/settings',
  'AIAvatarAdminPage': '/ai-avatar-layout/admin',
  'AIAvatarFeedbackPage': '/ai-avatar-layout/feedback',
  'AIAvatarScorecardPage': '/ai-avatar-layout/scorecard',
  'AIAvatarUnsoldPage': '/ai-avatar-layout/unsold',
  UserManagementPage: '/ai-avatar-layout/user-management',
  AIAvatarImpactHubPage: IMPACT_HUB_BASE_PATH,
  ImpactHubPage: IMPACT_HUB_BASE_PATH,
  AIAvatarMetricsFrameworkPage: IMPACT_HUB_BASE_PATH,
  MetricsFrameworkPage: IMPACT_HUB_BASE_PATH,
};

const normalizeAIAvatarRoutePath = (path: string): string => {
  const trimmed = (path || '').trim();
  if (!trimmed) return '';
  return trimmed.split('?')[0].split('#')[0];
};

const resolveAIAvatarRouteComponent = (
  routePath: string,
  componentName: string,
): React.ComponentType<any> | null => {
  if (componentName && routesMap[componentName]) {
    return routesMap[componentName];
  }
  if (routePath && aiAvatarRouteMap[routePath]) {
    return aiAvatarRouteMap[routePath];
  }
  const mappedPath = componentToRouteMap[componentName];
  if (mappedPath && aiAvatarRouteMap[mappedPath]) {
    return aiAvatarRouteMap[mappedPath];
  }
  return null;
};

export const AIAvatarLayout: React.FC = () => {
  // Inject Header reducer and saga for dealer functionality
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  const selectedDealer = useSelector(selectDealerSelected);
  
  // Check if dealer is selected
  const isDealerSelected = selectedDealer && 
    selectedDealer !== 'default' && 
    selectedDealer.dealer && 
    selectedDealer.dealer.trim() !== '';

  // Get routes from resource_mappings
  // Use state to allow re-rendering when routes change
  const [resourceMappings, setResourceMappings] = React.useState<any[]>(() => {
    try {
      return getRoutes() || [];
    } catch (error) {
      console.error('Error getting routes:', error);
      return [];
    }
  });

  // Update routes when server profile / resource mappings change
  React.useEffect(() => {
    const updateRoutes = () => {
      // Skip during logout so clearing the profile does not wipe routes and flash 404
      if (isLoggingOut()) return;
      try {
        const routes = getRoutes() || [];
        setResourceMappings(routes);
      } catch (error) {
        console.error('Error updating routes:', error);
      }
    };

    updateRoutes();

    window.addEventListener(SERVER_PROFILE_UPDATED_EVENT, updateRoutes);

    return () => {
      window.removeEventListener(SERVER_PROFILE_UPDATED_EVENT, updateRoutes);
    };
  }, []);

  // Get allowed routes for NewAIAvatarLayout
  const allowedRoutes = useMemo(() => {
    const allowed: Array<{ path: string; component: React.ComponentType<any> }> = [];
    const seenPaths = new Set<string>();

    resourceMappings.forEach((route: any) => {
      const rawPath = route.path || '';
      const routePath = normalizeAIAvatarRoutePath(rawPath);
      const componentName = route.component || '';

      if (!routePath.startsWith('/ai-avatar-layout')) {
        const mappedPath = componentToRouteMap[componentName];
        if (!mappedPath?.startsWith('/ai-avatar-layout')) {
          return;
        }
      }

      let finalPath =
        routePath.startsWith('/ai-avatar-layout')
          ? routePath
          : normalizeAIAvatarRoutePath(componentToRouteMap[componentName] || '');

      // Autopilot / Impact Hub are shared at app root — skip under this Switch
      if (
        finalPath === AUTOPILOT_BASE_PATH ||
        finalPath.startsWith(`${AUTOPILOT_BASE_PATH}/`) ||
        finalPath === LEGACY_AI_AVATAR_AUTOPILOT_PATH ||
        finalPath.startsWith(`${LEGACY_AI_AVATAR_AUTOPILOT_PATH}/`) ||
        finalPath === LEGACY_AI_AVATAR_MEASURE_PATH ||
        finalPath.startsWith(`${LEGACY_AI_AVATAR_MEASURE_PATH}/`) ||
        finalPath === IMPACT_HUB_BASE_PATH ||
        finalPath.startsWith(`${IMPACT_HUB_BASE_PATH}/`) ||
        finalPath === LEGACY_AI_AVATAR_IMPACT_HUB_PATH ||
        finalPath.startsWith(`${LEGACY_AI_AVATAR_IMPACT_HUB_PATH}/`)
      ) {
        return;
      }

      if (!finalPath.startsWith('/ai-avatar-layout') || seenPaths.has(finalPath)) {
        return;
      }

      const component = resolveAIAvatarRouteComponent(finalPath, componentName);
      if (component) {
        allowed.push({ path: finalPath, component });
        seenPaths.add(finalPath);
      }
    });

    return allowed;
  }, [resourceMappings]);

  const hasUserManagementAccess = useMemo(
    () =>
      resourceMappings.some((route: any) => {
        const routePath = route?.path || '';
        const routeComponent = route?.component || '';
        return (
          routeComponent === 'UserManagementPage' ||
          routePath === '/user-management' ||
          routePath.startsWith('/user-management?')
        );
      }),
    [resourceMappings],
  );

  return (
    <>
      <GlobalStyles
        styles={(theme) => ({
          // Global scrollbar styles for theme compatibility
          '*': {
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[500],
              },
            },
            '&::-webkit-scrollbar-corner': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
            },
          },
        })}
      />
      <AppProvider>
        <Layout>
          <Switch>
            {/* Feedback page is always accessible, even without dealer selection */}
            <Route exact path="/ai-avatar-layout/feedback" component={FeedbackPage} />

            {/* Legacy Autopilot paths → shared /autopilot */}
            <Route
              exact
              path={LEGACY_AI_AVATAR_MEASURE_PATH}
              render={() => <Redirect to={AUTOPILOT_BASE_PATH} />}
            />
            <Route
              exact
              path={`${LEGACY_AI_AVATAR_MEASURE_PATH}/ask-mia`}
              render={() => (
                <Redirect to={`${AUTOPILOT_BASE_PATH}/ask-mia`} />
              )}
            />
            <Route
              exact
              path={LEGACY_AI_AVATAR_AUTOPILOT_PATH}
              render={() => <Redirect to={AUTOPILOT_BASE_PATH} />}
            />
            <Route
              exact
              path={`${LEGACY_AI_AVATAR_AUTOPILOT_PATH}/ask-mia`}
              render={() => (
                <Redirect to={`${AUTOPILOT_BASE_PATH}/ask-mia`} />
              )}
            />
            <Route
              exact
              path={LEGACY_AI_AVATAR_IMPACT_HUB_PATH}
              render={({ location }) => (
                <Redirect
                  to={{
                    pathname: IMPACT_HUB_BASE_PATH,
                    search: location.search,
                  }}
                />
              )}
            />
            
            {/* Unsold Lead Details Page route - always available when dealer is selected */}
            {isDealerSelected && (
              <Route path="/ai-avatar-layout/unsold/lead/:leadId" component={UnsoldLeadDetailsPage} />
            )}
            
            {/* Dynamic routes from resource_mappings - only when dealer is selected */}
            {isDealerSelected && allowedRoutes.length > 0 &&
              allowedRoutes.map((route, index) => {
                // Skip feedback route as it's already handled above
                if (route.path === '/ai-avatar-layout/feedback') {
                  return null;
                }
                return (
                  <Route
                    key={`${route.path}-${index}`}
                    exact
                    path={route.path}
                    component={route.component}
                  />
                );
              })}

            {/* Legacy user-management route mapped to New AI layout path */}
            {isDealerSelected && hasUserManagementAccess && (
              <Route exact path="/ai-avatar-layout/user-management" component={UserManagementPage} />
            )}

            {/* Show dealer selection fallback if no dealer selected */}
            {!isDealerSelected && !isLoggingOut() && (
              <Route component={DealerSelectionFallback} />
            )}
            
            {/* 404 only when routes are loaded and path unmatched — never during logout */}
            {!isLoggingOut() && resourceMappings.length > 0 && (
              <Route component={NotFoundPage} />
            )}
          </Switch>
        </Layout>
      </AppProvider>
    </>
  );
};

